export interface RouteDetails {
  name: string;
  path: string;
}

export type MainKeys = 'home' | 'benefits' | 'idCards' | 'inbox';

export type AdditionalCategoryKeys =
  | 'profile'
  | 'benefits'
  | 'company'
  | 'account';

export type LegalKeys = 'terms' | 'privacyPolicy' | 'thirdPartyLicenses';

type AdditionalProfileKeys =
  | 'myProfile'
  | 'myFamily'
  | 'myBeneficiaries'
  | 'myIdCards'
  | 'contacts';

type AdditionalDocumentsKeys = 'acaDocuments';

type AdditionalBenefitsKeys = 'myBenefits';

type AdditionalCompanyKeys = 'myCompany';

type AdditionalAccountKeys =
  | 'settings'
  | 'securityPreferences'
  | 'legalDocuments';

type PublicKeys =
  | 'login'
  | 'mfa'
  | 'forgotPassword'
  | 'changePassword'
  | 'termsAndConditions'
  | 'legalDocuments'
  | 'thirdPartyLicenses'
  | 'privacyPolicy'
  | 'accessDenied';

type ToDoListKeys = 'eoi' | 'addYourDocuments';

export type RoutesSection<T extends string> = {
  [key in T]: RouteDetails;
};

interface Routes {
  home: RouteDetails;
  main: RoutesSection<MainKeys>;
  additional: {
    profile: RoutesSection<AdditionalProfileKeys>;
    documents: RoutesSection<AdditionalDocumentsKeys>;
    benefits: RoutesSection<AdditionalBenefitsKeys>;
    company: RoutesSection<AdditionalCompanyKeys>;
    account: RoutesSection<AdditionalAccountKeys>;
  };
  legalDocs: RoutesSection<LegalKeys>;
  public: RoutesSection<PublicKeys>;
  enrollment: RouteDetails;
  planSelect: RouteDetails;
  lifeEvent: RouteDetails;
  toDoList: RoutesSection<ToDoListKeys>;
}

export const ROUTES: Routes = {
  // route for the new home page that will replace the current source and lumos home pages
  home: {
    name: 'Home',
    path: '/home',
  },
  main: {
    home: {
      name: 'navigation.main:homeLinkTitle',
      path: '/',
    },
    benefits: {
      name: 'navigation.main:benefitsLinkTitle',
      path: '/benefits',
    },
    idCards: {
      name: 'navigation.main:myIdCardsLinkTitle',
      path: '/id-cards',
    },
    inbox: {
      name: 'navigation.main:inboxLinkTitle',
      path: '/inbox',
    },
  },
  additional: {
    profile: {
      myProfile: {
        name: 'navigation.additional:profile.myProfileLinkTitle',
        path: '/profile',
      },
      myFamily: {
        name: 'navigation.additional:profile.myFamilyLinkTitle',
        path: '/family',
      },
      myBeneficiaries: {
        name: 'navigation.additional:profile.myBeneficiariesLinkTitle',
        path: '/beneficiaries',
      },
      myIdCards: {
        name: 'navigation.additional:profile.myIdCardsLinkTitle',
        path: '/id-cards',
      },
      contacts: {
        name: 'navigation.additional:profile.contactsLinkTitle',
        path: '/contacts',
      },
    },
    documents: {
      acaDocuments: {
        name: 'navigation.additional:documents.acaDocumentsLinkTitle',
        path: '/aca-documents',
      },
    },
    benefits: {
      myBenefits: {
        name: 'navigation.additional:benefits.myBenefitsLinkTitle',
        path: '/benefits',
      },
    },
    company: {
      myCompany: {
        name: 'navigation.additional:company.myCompanyLinkTitle',
        path: '/my-company',
      },
    },
    account: {
      settings: {
        name: 'navigation.additional:account.settingsLinkTitle',
        path: '/settings',
      },
      securityPreferences: {
        name: 'navigation.additional:account.securityPreferencesTitle',
        path: '/security-preferences',
      },
      legalDocuments: {
        name: 'navigation.additional:account.legalDocumentsLinkTitle',
        path: '/legal-documents',
      },
    },
  },
  legalDocs: {
    terms: {
      name: 'pages.legalDocuments:termsAndConditionsLabel',
      path: '/terms-and-conditions/readonly',
    },
    privacyPolicy: {
      name: 'pages.legalDocuments:privacyPolicyLabel',
      path: '/privacy-policy',
    },
    thirdPartyLicenses: {
      name: 'pages.legalDocuments:Third-Party Licenses',
      path: '/third-party-license',
    },
  },
  toDoList: {
    eoi: {
      name: 'Evidence Of Insurability',
      path: '/eoi',
    },
    addYourDocuments: {
      name: 'navigation.enrollment:add-your-documents',
      path: '/add-your-documents',
    },
  },
  public: {
    login: {
      name: 'Login',
      path: '/login',
    },
    mfa: {
      name: 'Multi Factor Authentication',
      path: '/mfa',
    },
    forgotPassword: {
      name: 'Forgot Password',
      path: '/forgot-password',
    },
    changePassword: {
      name: 'Change Password',
      path: '/change-password',
    },
    termsAndConditions: {
      name: 'Terms and Conditions',
      path: '/terms-and-conditions',
    },
    legalDocuments: {
      name: 'Legal Documents',
      path: '/legal-documents',
    },
    thirdPartyLicenses: {
      name: 'Third-Party Licenses',
      path: '/third-party-license',
    },
    privacyPolicy: {
      name: 'Privacy Policy',
      path: '/privacy-policy',
    },
    accessDenied: {
      name: 'Access Denied',
      path: '/access-denied',
    },
  },
  enrollment: {
    name: 'Enrollment',
    path: '/enrollment',
  },
  planSelect: {
    name: 'Plan Select',
    path: '/enrollment/plan-select',
  },
  lifeEvent: {
    name: 'navigation.enrollment:life-event',
    path: '/life-event',
  },
};
